import type { JSONContent } from "~/stores/ArtifactPersistence";

/**
 * Converts ProseMirror JSON document to Markdown string.
 * Handles all standard markdown formatting including headings, lists,
 * bold, italic, links, code blocks, tables, etc.
 */
export function proseMirrorToMarkdown(doc: JSONContent): string {
  if (!doc.content) {
    return "";
  }

  return serializeNodes(doc.content);
}

function serializeNodes(nodes: JSONContent[]): string {
  return nodes.map((node) => serializeNode(node)).join("");
}

function serializeNode(node: JSONContent): string {
  switch (node.type) {
    case "paragraph":
      return serializeParagraph(node);
    case "heading":
      return serializeHeading(node);
    case "bulletList":
      return serializeBulletList(node);
    case "orderedList":
      return serializeOrderedList(node);
    case "listItem":
      return serializeListItem(node);
    case "blockquote":
      return serializeBlockquote(node);
    case "codeBlock":
      return serializeCodeBlock(node);
    case "table":
      return serializeTable(node);
    case "horizontalRule":
      return "---\n\n";
    case "text":
      return serializeText(node);
    case "hardBreak":
      return "  \n";
    default:
      // For unknown nodes, try to serialize their content
      if (node.content) {
        return serializeNodes(node.content);
      }
      return "";
  }
}

function serializeParagraph(node: JSONContent): string {
  if (!node.content || node.content.length === 0) {
    return "\n";
  }
  return `${serializeInlineContent(node.content)}\n\n`;
}

function serializeHeading(node: JSONContent): string {
  const level = node.attrs?.level || 1;
  const prefix = "#".repeat(level);
  const content = node.content ? serializeInlineContent(node.content) : "";
  return `${prefix} ${content}\n\n`;
}

function serializeBulletList(node: JSONContent, indent = 0): string {
  if (!node.content) return "";

  return node.content.map((item: JSONContent) => serializeListItem(item, "bullet", indent)).join("") + "\n";
}

function serializeOrderedList(node: JSONContent, indent = 0): string {
  if (!node.content) return "";

  return (
    node.content
      .map((item: JSONContent, index: number) => serializeListItem(item, "ordered", indent, index + 1))
      .join("") + "\n"
  );
}

function serializeListItem(
  node: JSONContent,
  listType: "bullet" | "ordered" = "bullet",
  indent = 0,
  index = 1,
): string {
  const indentStr = "  ".repeat(indent);
  const marker = listType === "bullet" ? "-" : `${index}.`;

  if (!node.content || node.content.length === 0) {
    return `${indentStr}${marker} \n`;
  }

  let result = "";
  for (let i = 0; i < node.content.length; i++) {
    const child = node.content[i];
    if (!child) continue;

    if (child.type === "paragraph") {
      // First paragraph in list item goes on the same line as marker
      if (i === 0) {
        const content = child.content ? serializeInlineContent(child.content) : "";
        result += `${indentStr}${marker} ${content}\n`;
      } else {
        // Subsequent paragraphs are indented
        const content = child.content ? serializeInlineContent(child.content) : "";
        result += `${indentStr}  ${content}\n\n`;
      }
    } else if (child.type === "bulletList") {
      result += serializeBulletList(child, indent + 1);
    } else if (child.type === "orderedList") {
      result += serializeOrderedList(child, indent + 1);
    } else if (child.type === "codeBlock") {
      // Code blocks in lists need proper indentation
      const lines = serializeCodeBlock(child).split("\n");
      result += lines.map((line) => `${indentStr}  ${line}`).join("\n") + "\n";
    } else {
      result += serializeNode(child);
    }
  }

  return result;
}

function serializeBlockquote(node: JSONContent): string {
  if (!node.content) return "";

  const lines = serializeNodes(node.content).trim().split("\n");
  return lines.map((line) => `> ${line}`).join("\n") + "\n\n";
}

function serializeCodeBlock(node: JSONContent): string {
  const language = node.attrs?.language || "";
  const content = node.content ? serializeNodes(node.content).trimEnd() : "";

  return `\`\`\`${language}\n${content}\n\`\`\`\n\n`;
}

function serializeTable(node: JSONContent): string {
  if (!node.content || node.content.length === 0) return "";

  const rows = node.content;
  let markdown = "";
  let isFirstRow = true;

  for (const row of rows) {
    if (!row.content) continue;

    const cells = row.content.map((cell: JSONContent) => {
      if (!cell.content) return "";
      return serializeInlineContent(cell.content).trim();
    });

    markdown += `| ${cells.join(" | ")} |\n`;

    // Add separator after first row (header row)
    if (isFirstRow) {
      const separator = cells.map(() => "---").join(" | ");
      markdown += `| ${separator} |\n`;
      isFirstRow = false;
    }
  }

  return markdown + "\n";
}

function serializeInlineContent(nodes: JSONContent[]): string {
  return nodes.map((node) => serializeInlineNode(node)).join("");
}

function serializeInlineNode(node: JSONContent): string {
  if (node.type === "text") {
    return serializeText(node);
  }
  if (node.type === "hardBreak") {
    return "  \n";
  }
  // For unknown inline nodes, try to serialize their content
  if (node.content) {
    return serializeInlineContent(node.content);
  }
  return "";
}

function serializeText(node: JSONContent): string {
  let text = node.text || "";

  if (!node.marks || node.marks.length === 0) {
    return text;
  }

  // Apply marks in order
  // Order matters: code should be innermost, then bold/italic, then link
  for (const mark of node.marks) {
    switch (mark.type) {
      case "code":
        text = `\`${text}\``;
        break;
      case "bold":
        text = `**${text}**`;
        break;
      case "italic":
        text = `*${text}*`;
        break;
      case "strike":
        text = `~~${text}~~`;
        break;
      case "link":
        if (mark.attrs?.href) {
          const title = mark.attrs.title ? ` "${mark.attrs.title}"` : "";
          text = `[${text}](${mark.attrs.href}${title})`;
        }
        break;
    }
  }

  return text;
}
